import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  EventNote as LeaveIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import * as actions from '../../redux/actions';

dayjs.locale('th');

const LEAVE_TYPE_LABELS = {
  SICK: 'ลาป่วย',
  PERSONAL: 'ลากิจ',
  VACATION: 'ลาพักร้อน',
  MATERNITY: 'ลาคลอด',
  ORDINATION: 'ลาบวช',
  UNPAID: 'ลาโดยไม่รับค่าจ้าง',
};

const PERIOD_LABELS = {
  FULL_DAY: 'เต็มวัน',
  MORNING: 'ครึ่งวันเช้า',
  AFTERNOON: 'ครึ่งวันบ่าย',
};

const STATUS_CONFIG = {
  PENDING: { label: 'รออนุมัติ', color: 'warning' },
  APPROVED: { label: 'อนุมัติ', color: 'success' },
  REJECTED: { label: 'ไม่อนุมัติ', color: 'error' },
  CANCELLED: { label: 'ยกเลิก', color: 'default' },
};

export default function LeaveRequestList({ title }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const me = useSelector((state) => state.me);
  const leaveRequest = useSelector((state) => state.leaveRequest);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dispatch(actions.leaveRequestAll({ me: me?.userData?._id }));
  }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('ยืนยันยกเลิกคำขอลาใช่หรือไม่?')) return;
    await dispatch(actions.leaveRequestPut(id, { status: 'CANCELLED' }));
    dispatch(actions.leaveRequestAll({ me: me?.userData?._id }));
  };

  const rows = leaveRequest?.rows || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <LeaveIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>{title || 'คำขอลา'}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => history.push('/profile/leave/add')}
        >
          ยื่นคำขอลา
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {leaveRequest?.isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box py={6} textAlign="center">
              <LeaveIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">ยังไม่มีรายการขอลา</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>รหัส</TableCell>
                    <TableCell>ประเภทการลา</TableCell>
                    <TableCell>วันที่ลา</TableCell>
                    <TableCell align="center">จำนวนวัน</TableCell>
                    <TableCell>ช่วงเวลา</TableCell>
                    <TableCell align="center">สถานะ</TableCell>
                    <TableCell align="center">จัดการ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const statusCfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.PENDING;
                    return (
                      <TableRow key={row._id} hover>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">{row.code || '-'}</Typography>
                        </TableCell>
                        <TableCell>{LEAVE_TYPE_LABELS[row.leave_type] || row.leave_type}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dayjs(row.start_date).format('DD/MM/YYYY')}
                            {row.start_date !== row.end_date && (
                              <> — {dayjs(row.end_date).format('DD/MM/YYYY')}</>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={600}>{row.total_days}</Typography>
                        </TableCell>
                        <TableCell>{PERIOD_LABELS[row.period] || row.period}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={statusCfg.label}
                            color={statusCfg.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="รายละเอียด">
                            <IconButton size="small" onClick={() => { setSelected(row); setDetailOpen(true); }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {row.status === 'PENDING' && (
                            <Tooltip title="ยกเลิกคำขอ">
                              <IconButton size="small" color="error" onClick={() => handleCancel(row._id)}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog รายละเอียด */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>รายละเอียดคำขอลา</DialogTitle>
        <DialogContent>
          {selected && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">รหัส</Typography>
                <Typography fontFamily="monospace">{selected.code || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">สถานะ</Typography>
                <Box>
                  <Chip
                    label={STATUS_CONFIG[selected.status]?.label}
                    color={STATUS_CONFIG[selected.status]?.color}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">ประเภทการลา</Typography>
                <Typography>{LEAVE_TYPE_LABELS[selected.leave_type]}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">ช่วงเวลา</Typography>
                <Typography>{PERIOD_LABELS[selected.period]}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">วันที่เริ่มลา</Typography>
                <Typography>{dayjs(selected.start_date).format('DD/MM/YYYY')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">วันที่สิ้นสุดลา</Typography>
                <Typography>{dayjs(selected.end_date).format('DD/MM/YYYY')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">จำนวนวันลา</Typography>
                <Typography fontWeight={700} variant="h6">{selected.total_days} วัน</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">เหตุผล</Typography>
                <Typography>{selected.reason}</Typography>
              </Grid>
              {selected.approval_note && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">หมายเหตุจากผู้อนุมัติ</Typography>
                  <Typography>{selected.approval_note}</Typography>
                </Grid>
              )}
              {selected.approver && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">ผู้อนุมัติ</Typography>
                  <Typography>
                    {selected.approver?.firstname_th || selected.approver?.firstname}{' '}
                    {selected.approver?.lastname_th || selected.approver?.lastname}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
